"use client"

import { authCookieAttributes, COOKIE_NAMES } from "utils/auth"
import { clearAuthCookie, setAuthCookie } from "utils/external/swayam/cookies"

interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
}

export const handleLogin = async () => {
  try {
    fetch(process.env.NEXT_PUBLIC_API_URL + "/api/sso", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        return response.json()
      })
      .then((res: any) => {
        window.location = res.redirect_url
      })
  } catch (error: any) {
    window.alert(error.message)
  }
}

export const handleOAuthLogin = async (provider: "microsoft" | "google") => {
  try {
    fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/oauth/login/${provider}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        return response.json()
      })
      .then((res: any) => {
        window.location = res.redirect_url
      })
  } catch (error: any) {
    window.alert(error.message)
  }
}

export const handleMicrosoftLogin = () => handleOAuthLogin("microsoft")
export const handleGoogleLogin = () => handleOAuthLogin("google")

export const refreshAccessToken = async () => {
  try {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      return
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/refresh_token_with_payload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      document.cookie = `${COOKIE_NAMES.ACCESS_TOKEN}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
      document.cookie = `${COOKIE_NAMES.REFRESH_TOKEN}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`

      // Redirect to the login page
      window.location.href = "/login"
      return
    }

    const { access_token, refresh_token } = (await response.json()) as RefreshTokenResponse

    // Update cookies with new tokens
    document.cookie = `${COOKIE_NAMES.ACCESS_TOKEN}=${access_token}; ${authCookieAttributes()}`
    document.cookie = `${COOKIE_NAMES.REFRESH_TOKEN}=${refresh_token}; ${authCookieAttributes()}`
  } catch (error) {
    console.error("Error refreshing access token:", error)
  }
}

// Clear any invalid cookies that might have been set
export const clearInvalidCookies = () => {
  const cookies = document.cookie.split(";")
  cookies.forEach((cookie) => {
    const [name, value] = cookie.trim().split("=")
    if (name && (value === "undefined" || value === "null" || value === "")) {
      clearAuthCookie(name)
    }
  })
}

export const internalJwtGenerate = async (email: string, name?: string) => {
  try {
    // Clear any invalid cookies first
    clearInvalidCookies()

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/internal/jwt/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email: email, name: name || "" }),
    })

    if (!response.ok) {
      console.error("Error generating internal JWT: Response not ok", response.status, response.statusText)
      return
    }

    const data = (await response.json()) as RefreshTokenResponse

    // Validate that we have the required tokens
    if (!data.access_token || !data.refresh_token) {
      console.error("Error generating internal JWT: Missing tokens in response", data)
      return
    }

    const { access_token, refresh_token } = data

    // Additional validation to ensure tokens are not undefined or empty
    if (access_token && access_token !== "undefined" && refresh_token && refresh_token !== "undefined") {
      setAuthCookie(COOKIE_NAMES.ACCESS_TOKEN, access_token)
      setAuthCookie(COOKIE_NAMES.REFRESH_TOKEN, refresh_token)
    } else {
      console.error("Error generating internal JWT: Invalid token values", { access_token, refresh_token })
    }
  } catch (error) {
    console.error("Error generating internal JWT:", error)
  }
}

export const ssoExchange = async (token: string, transactionId: string) => {
  try {
    // Clear any invalid cookies first
    clearInvalidCookies()

    const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/sso/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, transaction_id: transactionId }),
    })

    if (!response.ok) {
      console.error("Error exchanging SSO token: Response not ok", response.status, response.statusText)
      return
    }

    const { access_token, refresh_token } = (await response.json()) as RefreshTokenResponse

    if (access_token && access_token !== "undefined" && refresh_token && refresh_token !== "undefined") {
      setAuthCookie(COOKIE_NAMES.ACCESS_TOKEN, access_token)
      setAuthCookie(COOKIE_NAMES.REFRESH_TOKEN, refresh_token)
    } else {
      console.error("Error exchanging SSO token: Invalid token values")
    }
  } catch (error) {
    console.error("Error exchanging SSO token:", error)
  }
}

// Start polling
export const startTokenPolling = () => {
  refreshAccessToken()
}

export const getAccessToken = () => {
  const match = document.cookie.match(new RegExp("(^| )access_token=([^;]+)"))
  if (match) return match[2]
  return null
}

export const getRefreshToken = () => {
  const match = document.cookie.match(new RegExp("(^| )refresh_token=([^;]+)"))
  if (match) return match[2]
  return null
}

export const getExternalAccessToken = () => {
  const match = document.cookie.match(new RegExp("(^| )external_access_token=([^;]+)"))
  if (match) return match[2]
  return null
}
